import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";

import { cn } from "@/lib/utils";

interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "style"> {
  /** Whether the textarea should grow with its content. Defaults to true. */
  autosize?: boolean;
  /** Called when the user presses Cmd/Ctrl+Enter. When provided, Enter adds a newline and Cmd/Ctrl+Enter triggers this callback. */
  onSend?: () => void;
  /** Extra guard that must be true for Cmd/Ctrl+Enter to trigger onSend. Defaults to true. */
  canSend?: boolean;
  /** Minimum number of visible rows. */
  minRows?: number;
  /** Maximum number of visible rows before scrolling. */
  maxRows?: number;
  /** Inline styles for the textarea element. */
  style?: React.CSSProperties;
}

/**
 * Auto-resizing textarea built on react-textarea-autosize.
 * Optionally supports Cmd/Ctrl+Enter to send via the onSend prop.
 * @param props - TextareaProps
 * @returns A controlled textarea element with auto-resize behavior
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      onSend,
      autosize = true,
      canSend = true,
      onKeyDown,
      minRows,
      maxRows,
      rows,
      style,
      ...props
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const widthRef = useRef(0);
    const [, setWidthEpoch] = useState(0);

    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    // react-textarea-autosize only re-measures on re-render and window resize,
    // so a CSS width transition on an ancestor (e.g. collapsing the chat pane)
    // leaves the height computed at a mid-transition width. Re-render on
    // element width changes so it re-measures at the settled width.
    useEffect(() => {
      const node = innerRef.current;
      if (!autosize || !node) {
        return;
      }
      widthRef.current = node.offsetWidth;
      const observer = new ResizeObserver(() => {
        if (node.offsetWidth === widthRef.current) {
          return;
        }
        widthRef.current = node.offsetWidth;
        setWidthEpoch((epoch) => epoch + 1);
      });
      observer.observe(node);
      return () => observer.disconnect();
    }, [autosize]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (
          onSend &&
          e.key === "Enter" &&
          (e.metaKey || e.ctrlKey) &&
          canSend
        ) {
          e.preventDefault();
          onSend();
          return;
        }
        onKeyDown?.(e);
      },
      [onSend, canSend, onKeyDown],
    );

    const textareaClassName = cn(
      "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none",
      className,
    );

    if (!autosize) {
      return (
        <textarea
          className={textareaClassName}
          ref={ref}
          onKeyDown={handleKeyDown}
          rows={rows}
          style={style}
          {...props}
        />
      );
    }

    return (
      <TextareaAutosize
        className={textareaClassName}
        ref={setRefs}
        onKeyDown={handleKeyDown}
        minRows={minRows ?? rows}
        maxRows={maxRows}
        style={style as React.CSSProperties & { height?: number }}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
export type { TextareaProps };
